<template>
	<div>
		<div class="bar">
			<div class="flex flex-col items-baseline shrink-0">
				<div class="font-bold text-lg" style="line-height: 12px;margin-top:4px">Kontakter</div>
				<div class="text-muted-color text-xs hidden-xs">
					<span v-if="filtered.length !== phonebook.contacts.length">{{ filtered.length }} {{ t('phonebook.of') }}</span>
					{{ phonebook.contacts.length }}
				</div>
			</div>
			<div style="flex-grow:5;text-align: center;">
				<InputText v-model="filter" placeholder="Søg" size="small" class="search" type="search" :autofocus="true" />
			</div>
			<div class="flex gap-2 shrink-0">
				<Select v-model="type" :options="contactTypes" size="small" option-value="value" option-label="label" style="width: 120px" />
				<Select v-model="group" :options="groupOptions" size="small" option-value="_id" option-label="name" option-group-label="label" option-group-children="items"
					:placeholder="t('phonebook.groups.filterAll')" showClear :filter="availableGroups.length > 8" :disabled="availableGroups.length === 0" style="width: 170px">
					<template #option="{ option }">
						<div class="flex w-full gap-3">
							<span class="dotdotdot">{{ option.name }}</span>
							<span class="ml-auto text-muted-color text-sm">{{ phonebook.groupCounts.get(option._id) ?? 0 }}</span>
						</div>
					</template>
				</Select>
				<Button size="small" severity="secondary" :title="t('phonebook.groups.manage')" :aria-label="t('phonebook.groups.manage')" @click="manageGroups()" iconOnly><i class="p-button-icon pi pi-tags" /></Button>
				<Button size="small" @click="add()"><i class="p-button-icon pi pi-plus" /><span>{{ t('phonebook.newContact') }}</span></Button>
			</div>
		</div>

		<div class="top-columns">
			<div>&nbsp;</div>
			<div>Navn/Virksomhed</div>
			<div>Telefonnummer</div>
			<div class="hidden lg:block">E-mail</div>
			<div class="hidden xl:block">Adresse</div>
			<div class="hidden sm:block">Titel</div>
			<div class="hidden xl:block">{{ t('phonebook.groups.label') }}</div>
		</div>

		<div style="position: relative;">
			<div v-if="filtered.length === 0" class="text-muted-color text-center italic" style="position:absolute;top:10px;left:0;right:0;z-index:1000">{{ t('phonebook.noContacts') }}</div>
		</div>

		<VirtualScroller :items="filtered" :itemSize="40" class="w-full scroller-box" :style="{ height: scrollHeight }" :auto-size="true">
			<template v-slot:item="{ item } : { item: PbxPhonebookContact }">
				<div class="list-item" :class="{'list-item-selected': selectedItem === item}" @contextmenu="showContextMenu($event, item)" @click="showContextMenuDelayed($event, item)" @dblclick="edit(item)">
					<div>
						<GlyphIcon :icon="item.type === PbxPhonebookContactType.PERSON ? GlyphIconName['user'] : GlyphIconName['building']" size="18" />
					</div>
					<div>
						{{ item.name }}
						<span v-if="item.type  === PbxPhonebookContactType.PERSON && (item.givenName || item.familyName) && item.company" class="company-addon">({{ item.company }})</span>
					</div>
					<div>{{ item.phoneNumbers.length > 0 ? ss.cutNationalPrefix(item.phoneNumbers[0]?.number as string, true) : '' }}</div>
					<div class="hidden lg:block">{{ item.emailAddresses.length > 0 ? item.emailAddresses[0]?.address : '' }}</div>
					<div class="hidden xl:block">{{ item.addresses.length > 0 ? item.addresses[0]?.street + ', ' + item.addresses[0]?.zip + ' ' + item.addresses[0]?.city + (item.addresses[0]?.country && item.addresses[0]?.country !== 'DK' ? ', ' + iso3166.countryDK[item.addresses[0]?.country] : '') : '' }}</div>
					<div class="hidden sm:block">
						<span v-if="item.title">{{ item.title }}</span>
						<span v-if="!item.title && item.department">{{ item.department }}</span>
						<span v-if="item.department&&item.title" class="text-muted-color text-sm pl-1">({{ item.department }})</span>
					</div>
					<div class="hidden xl:block group-names" :title="groupText(item)">{{ groupText(item) }}</div>
				</div>
			</template>
		</VirtualScroller>

		<ContextMenu ref="contextMenu" :model="contextMenuItems" appendTo="body" @show="contextMenuShown" @hide="contextMenuHidden" popup />

	</div>
</template>

<script setup lang="ts">
import GlyphIcon from '@/components/GlyphIcon.vue'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useSettingsStore } from '@/stores/Settings'
import { useI18n } from 'vue-i18n'
import { type PbxPhonebookContact, PbxPhonebookContactType } from '@/TcxApi/PbxPhonebook'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { dynamicSort } from '@/utilities/Tools'
import { groupsForListType, validGroupFilter } from '@/utilities/phonebookGroups'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import iso3166 from '@/utilities/iso3166'
import type TieredMenu from 'primevue/tieredmenu'
import type { MenuItem } from 'primevue/menuitem'
import SipClient from '@/utilities/SipClient'
import globalEvents from '@/utilities/GlobalEvents'
import { guestContactPrefill } from '@/utilities/guestEntryPoints'
import { useGuestInvitesStore } from '@/stores/GuestInvites'
import logger from '@/utilities/Logger'
import { useApiStore } from '@/stores/Api'
import type { ErrorResponseDetail } from '@/TcxApi/ErrorResponse'

const phonebook = usePhonebookStore()
const ss = useSettingsStore()
const guestInvites = useGuestInvitesStore()
const api = useApiStore()
const sipClient = SipClient.instance
const { t } = useI18n()

enum ListTypes { ALL = 'ALL', SHARED = 'SHARED', PERSONAL = 'PERSONAL' }
const filter = ref<string>(ss.getGeneric('phonebookFilter') ?? '')
const type = ref<ListTypes>(ss.getGeneric('phonebookType') ?? ListTypes.ALL)
const group = ref<string | null>(ss.getGeneric<string | null>('phonebookGroup') ?? null)
const contactTypes = [
	{ value: 'ALL', label: t('phonebook.types.ALL') },
	{ value: 'SHARED', label: t('phonebook.types.SHARED') },
	{ value: 'PERSONAL', label: t('phonebook.types.PERSONAL') }
]
const contextMenu = ref<InstanceType<typeof TieredMenu>>()
const selectedItem = ref<PbxPhonebookContact | undefined>(undefined)
const contextMenuTimeout = ref<ReturnType<typeof setTimeout> | undefined>(undefined)
const contextMenuVisible = ref(false)
let lastContextMenuHide = new Date().getTime()
let selectedForDeletion: string | undefined = undefined

const contextMenuItems = computed(() => {
	const items: MenuItem[] = []

	if (!selectedItem.value) { return [] }

	if (selectedItem.value?.phoneNumbers.length > 1) {
		const numbers: MenuItem[] = []

		for (const number of selectedItem.value.phoneNumbers) {
			numbers.push({ label: ss.cutNationalPrefix(number.number, true), command: () => call(number.number) })
		}

		items.push({ label: t('phonebook.call'), icon: 'pi pi-phone', items: numbers })
	}

	if (selectedItem.value?.phoneNumbers.length === 1) {
		const number = selectedItem.value.phoneNumbers[0]?.number as string

		items.push({ label: t('phonebook.call'), icon: 'pi pi-phone', command: () => call(number) })
	}

	if (selectedItem.value?.emailAddresses.length > 1) {
		const emails: MenuItem[] = []

		for (const email of selectedItem.value.emailAddresses) {
			emails.push({ label: email.address, command: () => window.open(`mailto:${email.address}`, '_blank') })
		}

		items.push({ label: t('phonebook.email'), icon: 'pi pi-envelope', items: emails })
	}
	if (selectedItem.value?.emailAddresses.length === 1) {
		const email = selectedItem.value.emailAddresses[0]?.address as string

		items.push({ label: t('phonebook.email'), icon: 'pi pi-envelope', command: () => window.open(`mailto:${email}`, '_blank') })
	}

	if (selectedItem.value?.addresses.length > 0) {
		items.push({ label: t('phonebook.showOnMap'), icon: 'pi pi-map-marker', command: () => window.open(`https://www.google.com/maps/search/?api=1&query=${selectedItem.value?.addresses[0]?.street} ${selectedItem.value?.addresses[0]?.zip} ${selectedItem.value?.addresses[0]?.city}`, '_blank') })
	}

	if (guestInvites.canCreateGuestInvites) {
		const prefill = guestContactPrefill(selectedItem.value)

		items.push({ label: t('guest.inviteContact'), icon: 'pi pi-video', command: () => globalEvents.emit('CommonGuestInviteModal', prefill) })
	}

	if (items.length > 0) {
		items.push({ separator: true })
	}

	items.push({ label: t('common.edit'), icon: 'pi pi-pencil', command: () => edit() })
	items.push({ label: t('common.delete'), icon: 'pi pi-trash', command: () => remove() })

	return items
})

/** The groups the group filter offers - they follow the type filter */
const availableGroups = computed(() => groupsForListType(phonebook.groups, type.value, ss.auth?.employee))

/** The group filter's options, under the headings Fælles grupper and Mine grupper */
const groupOptions = computed(() => {
	const shared = availableGroups.value.filter(option => !option.employee)
	const mine = availableGroups.value.filter(option => option.employee)
	const options = []

	if (shared.length > 0) { options.push({ label: t('phonebook.groups.customer'), items: shared }) }
	if (mine.length > 0) { options.push({ label: t('phonebook.groups.mine'), items: mine }) }
	return options
})

/** The group to filter on - none while the groups are not loaded, so a remembered group cannot empty the list */
const activeGroup = computed(() => (phonebook.groupsLoaded ? validGroupFilter(group.value, availableGroups.value) : null))

const scrollHeight = computed(() => {
	if (ss.settings.showStatusPanel) {
		return 'calc(100vh - 158px)'
	} else {
		return 'calc(100vh - 138px)'
	}
})

const filtered = computed(() => {
	let res: PbxPhonebookContact[] = []

	if (filter.value) {
		const regexps = filter.value.toLowerCase().split(' ').map(part => part.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).filter(part => part).map(part => new RegExp(part))

		res = phonebook.contacts.filter(contact => {
			return regexps.every(regexp => regexp.test(contact.search as string))
		})
	} else {
		res = phonebook.contacts
	}
	if (type.value === ListTypes.PERSONAL) {
		res = res.filter(contact => contact.employee)
	} else if (type.value === ListTypes.SHARED) {
		res = res.filter(contact => !contact.employee)
	}
	if (activeGroup.value) {
		const id = activeGroup.value

		res = res.filter(contact => contact.groups?.includes(id))
	}

	res.sort(dynamicSort('name'))
	return res
})

function contextMenuShown() {
	contextMenuVisible.value = true
}

function contextMenuHidden() {
	contextMenuVisible.value = false
	selectedItem.value = undefined
	lastContextMenuHide = new Date().getTime()
}

function showContextMenu(event: MouseEvent, item: PbxPhonebookContact) {
	event.preventDefault()
	selectedItem.value = item
	contextMenu.value?.toggle(event)
}

function showContextMenuDelayed(event: MouseEvent, item: PbxPhonebookContact) {
	event.preventDefault()

	if (contextMenuVisible.value || new Date().getTime() - lastContextMenuHide < 200) {
		contextMenu.value?.hide()
		selectedItem.value = undefined
		return
	}

	selectedItem.value = item

	contextMenuTimeout.value = setTimeout(() => {
		if (contextMenuTimeout.value) {
			contextMenuTimeout.value = undefined
			contextMenu.value?.toggle(event)
		}
	}, 200)
}

function edit(item?: PbxPhonebookContact) {
	if (contextMenuTimeout.value) {
		clearTimeout(contextMenuTimeout.value)
		contextMenuTimeout.value = undefined
	}

	if (contextMenuVisible.value) { contextMenu.value?.hide() }

	if (!item && selectedItem.value) {
		item = selectedItem.value
		selectedItem.value = undefined
	}

	// Show modal with contact details
	if (item) {
		globalEvents.emit('Phonebook:Show', item._id)
	}
}

function add() {
	// Show modal with new contact form
	globalEvents.emit('Phonebook:CreateNew')
}

function manageGroups() {
	globalEvents.emit('CommonPhonebookGroupsModal')
}

/**
 * The names of a contact's groups, for the Grupper column
 * @param contact The contact
 * @returns The names, comma separated
 */
function groupText(contact: PbxPhonebookContact) {
	return phonebook.contactGroupNames(contact).join(', ')
}

/**
 * Extracts the name from a contact, based on the type.
 * @param contact Contact to extract the name from
 * @returns The name of the contact
 */
function contactToName(contact: PbxPhonebookContact) {
	if (contact.type === PbxPhonebookContactType.PERSON) {
		return ((contact.givenName ?? '') + ' ' + (contact.familyName ?? '')).trim()
	} else {
		return contact.company ?? ''
	}
}

function remove() {
	if (selectedItem.value) {
		selectedForDeletion = selectedItem.value._id
		globalEvents.emit('CommonQuestionModal', {
			header: t('phonebook.deleteContactHeader'),
			message: t('phonebook.deleteContactMessage').replace('%NAME%', contactToName(selectedItem.value)),
			ok: t('common.delete'),
			cancel: t('common.cancel'),
			emit: 'Phonebook:Delete',
			emitData: selectedItem.value._id
		})
	}
}

async function removeDo(id: string) {
	if (id.length === 24 && selectedForDeletion === id) {
		selectedForDeletion = undefined

		try {
			await phonebook.remove(id)
		}
		catch (err) {
			globalEvents.emit('CommonErrorModal', { header: t('phonebook.header'), message: t('phonebook.errorUnknown') + ' ' + (err as ErrorResponseDetail).message })
		}
	}
}

async function call(number: string) {
	logger.info(`Phonebook: User calling ${number}`)
	globalEvents.emit('SelectTopbarButton', 0)

	if (ss.settings.softphoneId) {
		sipClient.call(number)
	} else {
		try { await api.get(`/pbx/app/action/call?device=${ss.settings.phoneId}&target=${encodeURIComponent(number)}`) }
		catch (_) { globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') }) }
	}
}

function deselectWhenEditIsClosed(id: string) {
	if (selectedItem.value?._id === id) {
		selectedItem.value = undefined
	}
}

watch(filter, (newValue: string) => {
	ss.setGeneric('phonebookFilter', newValue)
})

watch(type, (newValue: ListTypes) => {
	ss.setGeneric('phonebookType', newValue)
})

watch(group, (newValue: string | null) => {
	ss.setGeneric('phonebookGroup', newValue ?? null)
})

// A remembered group that was deleted, or that the type filter rules out, falls back to all groups
watch([availableGroups, () => phonebook.isReady, () => phonebook.groupsLoaded], () => {
	if (phonebook.isReady && phonebook.groupsLoaded && group.value !== activeGroup.value) {
		group.value = activeGroup.value
	}
}, { immediate: true })

onMounted(() => {
	globalEvents.on('Phonebook:Close', deselectWhenEditIsClosed)
	globalEvents.on('Phonebook:Delete', removeDo)
})
onUnmounted(() => {
	globalEvents.off('Phonebook:Close', deselectWhenEditIsClosed)
	globalEvents.off('Phonebook:Delete', removeDo)
})
</script>

<style scoped>
.bar {
	background: radial-gradient(circle at left top, var(--p-surface-100), var(--p-surface-300));
	height: 40px;
	padding-left: 10px;
	padding-right: 10px;
	padding-top: 3px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	transition: 500ms;
	gap: 40px;
	position: sticky;
	z-index: 99;
}
.search {
	width: 50vh;
	max-width: 500px;
}
.top-columns {
	background: var(--bg-color-dashboard-header-darker);
	color: var(--txt-color-dashboard-header);
	box-shadow: 0px 1px 5px 0px rgba(0,0,0,0.25);
	height: 23px;
	display: grid;
	grid-template-columns: 30px 2fr 1fr 1fr 2fr 1fr 1fr;
	border-bottom: 1px solid var(--p-form-field-border-color);
	padding-top: 5px;
	position: sticky;
	z-index: 98;
	transition: 500ms;
}
.top-columns > div {
	text-align: left;
	padding-left: 5px;
	font-size: 11px;
	border-left: 1px solid var(--p-form-field-border-color);
}
.top-columns:last-child {
	border-right: 1px solid var(--p-form-field-border-color);
}
.list-item {
	display: grid;
	grid-template-columns: 30px 2fr 1fr 1fr 2fr 1fr 1fr;
	height: 30px;
	border-bottom: 1px solid var(--p-form-field-border-color);
	cursor: pointer;
	background-color: var(--p-surface-50);
	transition: background-color 500ms ease-out;
}
@media(max-width: 1279px) {
	.top-columns {
		grid-template-columns: 30px 2fr 1fr 1fr 1fr;
	}
	.list-item {
		grid-template-columns: 30px 2fr 1fr 1fr 1fr;
	}
}
@media(max-width: 1023px) {
	.top-columns {
		grid-template-columns: 30px 2fr 1fr 1fr;
	}
	.list-item {
		grid-template-columns: 30px 2fr 1fr 1fr;
	}
}
@media(max-width: 639px) {
	.top-columns {
		grid-template-columns: 30px 2fr 1fr;
	}
	.list-item {
		grid-template-columns: 30px 2fr 1fr;
	}
}
.list-item:nth-child(odd) {
	background: #00000010;
}
.list-item:hover {
	background-color: var(--p-surface-300);
	/* color: var(--p-primary-contrast-color) !important; */
	text-shadow: 1px 1px #00000020;
	transition: background-color 10ms ease-in;
}
.list-item > div {
	text-align: left;
	padding-left: 5px;
	border-left: 1px solid var(--p-form-field-border-color);
	align-content: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.company-addon, .group-names {
	color: var(--p-text-muted-color);
	font-size: 0.875rem;
	line-height: 1.25rem;
}
.list-item-selected {
	background-image: var(--topbar-search-result-selected) !important;
	color: var(--p-primary-contrast-color) !important;
	text-shadow: 1px 1px #00000020;
}
.list-item-selected > div > span.company-addon, .list-item-selected > div.group-names {
	color: var(--p-primary-contrast-color);
}

.scroller-box {
	background: var(--p-primary-contrast-color);
}
@media(max-width: 1100px) {
	.search {
		width: 100%;
	}
}
@media(max-width: 800px) {
	.hidden-xs {
		display: none;
	}
	.bar {
		gap: 10px;
	}
}
@media(prefers-color-scheme: dark) {
	.bar {
		background: radial-gradient(circle at left top, var(--p-surface-800), var(--p-surface-950));
	}
	.scroller-box {
		background: var(--p-primary-contrast-color)
	}
	.list-item:nth-child(odd) {
		background: #00000040;
	}
	.top-columns {
		box-shadow: 0px 1px 5px 0px rgba(255,255,255,0.25);
		border-bottom: 1px solid #333333;
	}
	.top-columns > div {
		border-left: 1px solid #333333;
	}
	.top-columns:last-child {
		border-right: 1px solid #333333;
	}
	.list-item {
		border-bottom: 1px solid #333333;
		background-color: var(--p-surface-800);
		transition: background-color 500ms ease-out;
	}
	.list-item:hover {
		background-color: var(--p-surface-700);
		/* color: var(--p-primary-contrast-color) !important; */
		text-shadow: 1px 1px #00000020;
		transition: background-color 10ms ease-in;
	}
	.list-item > div {
		border-left: 1px solid #333333;
	}
	.list-item-selected {
		background-image: radial-gradient(circle at left top, var(--p-primary-500), var(--p-primary-600)) !important;
		color: var(--p-primary-contrast-color) !important;
		text-shadow: 1px 1px #00000020;
	}

	.list-item-selected>div>span.company-addon, .list-item-selected>div.group-names {
		color: var(--p-primary-contrast-color);
	}
}
</style>
